import { authenticatedFetch, extractErrorMessage } from '@/admin/lib/api';
import type {
  AdminChat,
  AdminChatDetailFilters,
  AdminChatFacetsResponse,
  AdminChatIdentityCandidate,
  AdminChatMetricSummary,
  AdminChatSearchFilters,
  AdminChatTraceResponse,
  AdminChatsResponse,
  AdminConversationResponse,
  AdminConversationsResponse,
  TraceEvent,
  TraceRequest,
  TraceTimingBreakdown,
} from './types';

export type {
  AdminChat,
  AdminChatDetailFilters,
  AdminChatFacetsResponse,
  AdminChatIdentityCandidate,
  AdminChatMetricSummary,
  AdminChatSearchFilters,
  AdminChatTraceResponse,
  AdminChatsResponse,
  AdminConversationResponse,
  AdminConversationsResponse,
  TraceEvent,
  TraceRequest,
  TraceTimingBreakdown,
};

export interface AdminChatAmbiguousIdentityErrorPayload {
  error: {
    code: 'ambiguous_chat_identity';
    message: string;
  };
  candidates: AdminChatIdentityCandidate[];
}

export class AdminChatAmbiguousIdentityError extends Error {
  candidates: AdminChatIdentityCandidate[];

  constructor(message: string, candidates: AdminChatIdentityCandidate[]) {
    super(message);
    this.name = 'AdminChatAmbiguousIdentityError';
    this.candidates = candidates;
  }
}

function appendOptionalQuery(
  query: URLSearchParams,
  key: string,
  value: string | number | boolean | null | undefined
) {
  if (value === undefined || value === null || value === '') return;
  query.set(key, String(value));
}

function adminChatQuery(params?: object) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    appendOptionalQuery(
      query,
      key,
      value as string | number | boolean | null | undefined
    );
  }
  const suffix = query.toString();
  return suffix ? `?${suffix}` : '';
}

async function adminJSON<T>(path: string, fallback: string): Promise<T> {
  const response = await authenticatedFetch(path);
  if (!response.ok) {
    throw new Error(await extractErrorMessage(response, fallback));
  }
  return response.json();
}

export function fetchAdminChats(
  filters?: AdminChatSearchFilters
): Promise<AdminChatsResponse> {
  return adminJSON(
    `/v1/admin/chat-analytics/chats${adminChatQuery(filters)}`,
    'Failed to fetch chats'
  );
}

export function fetchAdminConversations(
  filters?: AdminChatSearchFilters
): Promise<AdminConversationsResponse> {
  return adminJSON(
    `/v1/admin/chat-analytics/conversations${adminChatQuery(filters)}`,
    'Failed to fetch conversations'
  );
}

export function fetchAdminConversation(
  conversationId: string,
  filters?: Pick<
    AdminChatSearchFilters,
    | 'tenant_id'
    | 'agent_id'
    | 'agent_group_id'
    | 'user_id'
    | 'session_id'
  > & {
    session_run_id?: string;
    limit?: number;
  }
): Promise<AdminConversationResponse> {
  return authenticatedFetch(
    `/v1/admin/chat-analytics/conversations/${encodeURIComponent(conversationId)}${adminChatQuery(filters)}`
  ).then(async response => {
    if (response.status === 409) {
      const payload =
        (await response.json()) as AdminChatAmbiguousIdentityErrorPayload;
      throw new AdminChatAmbiguousIdentityError(
        payload.error?.message || 'Multiple chats match this conversation id',
        payload.candidates || []
      );
    }
    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(response, 'Failed to fetch conversation')
      );
    }
    return response.json();
  });
}

export function fetchAdminConversationTrace(
  conversationId: string,
  filters?: Pick<
    AdminChatSearchFilters,
    'tenant_id' | 'agent_id' | 'agent_group_id' | 'user_id' | 'session_id'
  > & {
    session_run_id?: string;
    request_id?: string;
    limit?: number;
  }
): Promise<AdminChatTraceResponse> {
  return authenticatedFetch(
    `/v1/admin/chat-analytics/conversations/${encodeURIComponent(conversationId)}/trace${adminChatQuery(filters)}`
  ).then(async response => {
    if (response.status === 409) {
      const payload =
        (await response.json()) as AdminChatAmbiguousIdentityErrorPayload;
      throw new AdminChatAmbiguousIdentityError(
        payload.error?.message || 'Multiple chats match this conversation id',
        payload.candidates || []
      );
    }
    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(
          response,
          'Failed to fetch conversation trace'
        )
      );
    }
    return response.json();
  });
}

export function fetchAdminChatTrace(
  sessionId: string,
  filters?: Pick<
    AdminChatDetailFilters,
    'tenant_id' | 'agent_id' | 'user_id'
  > & {
    request_id?: string;
    limit?: number;
  }
): Promise<AdminChatTraceResponse> {
  return authenticatedFetch(
    `/v1/admin/chat-analytics/chats/${encodeURIComponent(sessionId)}/trace${adminChatQuery(filters)}`
  ).then(async response => {
    if (response.status === 409) {
      const payload =
        (await response.json()) as AdminChatAmbiguousIdentityErrorPayload;
      throw new AdminChatAmbiguousIdentityError(
        payload.error?.message || 'Multiple chats match this session id',
        payload.candidates || []
      );
    }
    if (!response.ok) {
      throw new Error(
        await extractErrorMessage(response, 'Failed to fetch chat trace')
      );
    }
    return response.json();
  });
}

export function fetchAdminChatFacets(
  limit = 100
): Promise<AdminChatFacetsResponse> {
  return adminJSON(
    `/v1/admin/chat-analytics/facets${adminChatQuery({ limit })}`,
    'Failed to fetch chat facets'
  );
}
